import Preferences from '@/creditflowresearch/domain/Preferences/Preferences';
import { PreferenceFamily } from '@/creditflowresearch/domain/Preferences/PreferenceSource';
import CrossProductMappingService from '@/creditflowresearch/domain/Preferences/CrossProductMappingService';
import {
  TARGET_FAMILY_CONFIG,
  type MappingTargetFamily,
} from '@/creditflowresearch/domain/Preferences/crossProductMappingConfig';
import { ABS_PRODUCT_ID, HYC_PRODUCT_ID, IGC_PRODUCT_ID } from '@/creditflowresearch/domain/Product/constants';

export enum MenuPanelKind {
  ABS = 'ABS',
  HYC = 'HYC',
  IGC = 'IGC',
}

export const MENU_REGION_PRIORITY: string[] =
  TARGET_FAMILY_CONFIG[PreferenceFamily.ABS].menuSingleSelectPriority;

export const MENU_CORPORATE_MARKET_PRIORITY: string[] =
  TARGET_FAMILY_CONFIG[PreferenceFamily.CORPORATE].menuSingleSelectPriority;

const PANEL_KIND_BY_PRODUCT_ID: Record<string, MenuPanelKind> = {
  [ABS_PRODUCT_ID]: MenuPanelKind.ABS,
  [HYC_PRODUCT_ID]: MenuPanelKind.HYC,
  [IGC_PRODUCT_ID]: MenuPanelKind.IGC,
};

export const menuPanelKindFromProductId = (productId: string): MenuPanelKind | null =>
  PANEL_KIND_BY_PRODUCT_ID[productId] ?? null;

const ALWAYS_ACCESSIBLE = (): boolean => true;

const findFirstSelectedAndAccessibleByPriority = (
  ids: string[],
  priority: string[],
  isAccessible: (id: string) => boolean,
): string | null => {
  const selected = new Set(ids);

  return priority.find((id) => selected.has(id) && isAccessible(id)) ?? null;
};

const TARGET_FAMILY_BY_PANEL: Record<MenuPanelKind, MappingTargetFamily> = {
  [MenuPanelKind.ABS]: PreferenceFamily.ABS,
  [MenuPanelKind.HYC]: PreferenceFamily.CORPORATE,
  [MenuPanelKind.IGC]: PreferenceFamily.CORPORATE,
};

const PRIORITY_BY_PANEL: Record<MenuPanelKind, string[]> = {
  [MenuPanelKind.ABS]: MENU_REGION_PRIORITY,
  [MenuPanelKind.HYC]: MENU_CORPORATE_MARKET_PRIORITY,
  [MenuPanelKind.IGC]: MENU_CORPORATE_MARKET_PRIORITY,
};

class MenuRegionResolver {
  static resolveAbsRegionId(
    absRegionIds: string[],
    isAccessible: (id: string) => boolean = ALWAYS_ACCESSIBLE,
  ): string | null {
    return findFirstSelectedAndAccessibleByPriority(
      absRegionIds,
      MENU_REGION_PRIORITY,
      isAccessible,
    );
  }

  static resolveCorporateMarketId(
    corporateMarketIds: string[],
    isAccessible: (id: string) => boolean = ALWAYS_ACCESSIBLE,
  ): string | null {
    return findFirstSelectedAndAccessibleByPriority(
      corporateMarketIds,
      MENU_CORPORATE_MARKET_PRIORITY,
      isAccessible,
    );
  }

  static resolveForPanel(
    preferences: Preferences | null,
    panelKind: MenuPanelKind,
    isAccessible: (id: string) => boolean = ALWAYS_ACCESSIBLE,
  ): string | null {
    const mappedIds = CrossProductMappingService.mapForTarget(
      preferences,
      TARGET_FAMILY_BY_PANEL[panelKind],
    );

    if (!mappedIds.length) {
      return null;
    }

    return findFirstSelectedAndAccessibleByPriority(
      mappedIds,
      PRIORITY_BY_PANEL[panelKind],
      isAccessible,
    );
  }
}

export default MenuRegionResolver;
